// src/utils/postLoginRedirect.ts
//
// Persists the page a logged-out user was trying to reach, so that after the
// Microsoft SSO round-trip (a full-page redirect) we can send them back there
// instead of always landing on /dashboard. Stored in sessionStorage because
// React Router in-memory state does not survive the external redirect.

const POST_LOGIN_REDIRECT_KEY = 'pit_post_login_redirect';

/**
 * True only for safe in-app paths: a single leading slash, no protocol, no
 * protocol-relative "//host" (which would be an open redirect off-site).
 */
export function isSafeAppPath(path: unknown): path is string {
  return (
    typeof path === 'string' &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.includes('://')
  );
}

/** Store the intended path (e.g. location.pathname + location.search). */
export function setPostLoginRedirect(path: string): void {
  try {
    if (isSafeAppPath(path) && path !== '/login') {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
    }
  } catch {
    // sessionStorage unavailable (private mode / SSR) — non-fatal.
  }
}

/**
 * Read and clear the stored path. Returns a safe app path, or null if none
 * (or if the stored value failed validation).
 */
export function consumePostLoginRedirect(): string | null {
  try {
    const value = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return isSafeAppPath(value) ? value : null;
  } catch {
    return null;
  }
}
